import React, { useEffect, useContext, useState } from "react";
import { FiPlus, FiFilter } from "react-icons/fi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { useCourses } from "./Hooks/useCourses";
import { CourseCard, ProgressOverviewTable } from "./Components";
import { CREATE_COURSE, EDIT_COURSE } from "../../routes";
import { UserContext } from "../../Context";
import { COURSE_LIST_TABS } from "../Training/constants";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import { getAllUsers } from "../../Services/UserService";
import { getAllCourses } from "../../Services/trainingService";
import InfiniteScroll from "react-infinite-scroll-component";

const CourseListPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isHR = ["HR", "DIRECTOR_MANAGEMENT"].includes(user?.role);

  const [activeTab, setActiveTab] = useState(COURSE_LIST_TABS.COURSES);
  const {
    loading, courses, fetchCourses, loadMoreCourses, resetCourses, removeCourse, pagination,
  } = useCourses();

  const [filters, setFilters] = useState({
    user: "",
    course: "",
  });
  const fetchUsers = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getAllUsers({ search });
    return res?.data?.results || [];
  };


  // TODO: Call backend api of all courses
  const fetchCourseOptions = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getAllCourses({ search });
    return res?.data?.results ?? [];
  };

  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [selectedCourseObj, setSelectedCourseObj] = useState(null);

  /* ---------------- HANDLERS ---------------- */
  const handleEdit = (courseId) => {
    navigate(EDIT_COURSE.replace(":courseEditId", courseId));
  };

  /* ---------------- FETCH COURSES ---------------- */
  useEffect(() => {
    if (activeTab === COURSE_LIST_TABS.COURSES) {
      resetCourses();
      fetchCourses({ offset: 0, limit: pagination.limit }, true);
    }
  }, [activeTab]);

  /* ---------------- FILTER ACTIONS ---------------- */
  const handleApplyFilters = () => {
    setFilters({
      user: tempFilters.user || "",
      course: tempFilters.course || "",
    });
    setShowFilter(false);
  };

  const handleClearFilters = () => {
    setTempFilters({ user: "", course: "" });
    setFilters({ user: "", course: "" });
    setSelectedUserObj(null);
    setSelectedCourseObj(null);
    setShowFilter(false);
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  return (
    <div className="px-2 py-6">
      {/* ---------------- HEADER ---------------- */}
      <div className="mb-6 flex flex-col md:flex-row justify-between">
        {/* Title */}
        <h2 className="text-xl font-semibold text-primary mb-5 md:mb-0">
          {isHR ? "Course Management" : "Continue Learning"}
        </h2>

        {isHR && (
          <div className="flex flex-row md:items-center justify-between md:gap-8">
            {/* Tabs */}
            <div className="flex gap-5 border-b overflow-x-auto mt-2 md:mt-0 ml-1 md:ml-4">
              <button
                onClick={() => setActiveTab(COURSE_LIST_TABS.COURSES)}
                className={`pb-2 whitespace-nowrap font-medium ${
                  activeTab === COURSE_LIST_TABS.COURSES
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500"
                }`}
              >
                Courses
              </button>

              <button
                onClick={() => setActiveTab(COURSE_LIST_TABS.PROGRESS)}
                className={`pb-2 whitespace-nowrap font-medium ${
                  activeTab === COURSE_LIST_TABS.PROGRESS
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500"
                }`}
              >
                Progress
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              {activeTab === COURSE_LIST_TABS.PROGRESS && (
                <button
                  className="px-3 py-2 bg-primary text-white rounded-lg font-semibold shadow hover:bg-green-600"
                  onClick={() => {
                    setTempFilters(filters);
                    setShowFilter(true);
                  }}
                >
                  <FiFilter />
                </button>
              )}

              <button
                onClick={() => navigate(CREATE_COURSE)}
                className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg font-semibold shadow hover:bg-green-600"
              >
                <FiPlus />
                <span className="hidden sm:inline">Create</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---------------- COURSES TAB ---------------- */}
      {activeTab === COURSE_LIST_TABS.COURSES && (
        <>
          <div id="courseScrollContainer" className="h-[70vh] overflow-y-auto">
            <InfiniteScroll
              dataLength={courses.length}
              next={loadMoreCourses}
              hasMore={courses.length < pagination.count}
              loader={
                <div className="text-center py-6 text-gray-500">
                  Loading more courses...
                </div>
              }
              endMessage={
                <div className="text-center py-6 text-gray-400">
                  No more courses
                </div>
              }
              scrollableTarget="courseScrollContainer"
            >
              <div className="flex flex-wrap gap-6">
                {courses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isHR={isHR}
                    onEdit={() => handleEdit(course.id)}
                    onDelete={removeCourse}
                  />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* ---------------- PROGRESS TAB ---------------- */}
      {activeTab === COURSE_LIST_TABS.PROGRESS && (
        <>
          {showFilter && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
                {/* 🔹 USER FILTER */}
                <SearchableDropdown
                  label="User"
                  placeholder="Search user"
                  fetchFunction={fetchUsers}
                  labelKey="full_name"
                  valueKey="id"
                  includeAllOption
                  value={selectedUserObj}
                  onSelect={(user) => {
                    setSelectedUserObj(user);
                    setTempFilters((prev) => ({
                      ...prev,
                      user: user?.id || "",
                    }));
                  }}
                />

                {/*  COURSE FILTER */}
                <SearchableDropdown
                  label="Course"
                  placeholder="Search course"
                  fetchFunction={fetchCourseOptions}
                  labelKey="title"
                  valueKey="id"
                  includeAllOption
                  value={selectedCourseObj}
                  onSelect={(course) => {
                    setSelectedCourseObj(course);
                    setTempFilters((prev) => ({
                      ...prev,
                      course: course?.id || "",
                    }));
                  }}
                />

                {/* 🔹 ACTION BUTTONS */}
                <div className="flex justify-between items-center pt-4">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                      onClick={handleCancelFilters}
                    >
                      Cancel
                    </button>

                    <button
                      className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className="border rounded-lg bg-white
                       h-[70vh] md:h-[65vh]
                       overflow-y-auto"
          >
            <ProgressOverviewTable filters={filters} />
          </div>
        </>
      )}
    </div>
  );
};

export default CourseListPage;
